// frontend/src/components/portfolio/BlockCanvas.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Chip,
  Checkbox,
  Snackbar,
  Alert
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Flag as FlagIcon,
  BarChart as BarChartIcon,
  Work as WorkIcon,
  CardGiftcard as CardGiftcardIcon,
  PhotoLibrary as PhotoLibraryIcon,
  FormatQuote as FormatQuoteIcon,
  TextFields as TextFieldsIcon,
  Email as EmailIcon,
  Group as GroupIcon,
  LayersClear as EmptyIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  Visibility as VisibilityIcon,
  Timeline as TimelineIcon,
  PlayCircle as PlayCircleIcon,
  Link as LinkIcon,
  AccountTree as AccountTreeIcon,
  FileCopy as CopyIcon,
  LibraryMusic as ArtistSearchIcon
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { BlockData } from '../../types/Portfolio';
import { getBlockByType } from '../../utils/blockRegistry';
import CopyBlocksModal from './CopyBlocksModal';

interface BlockCanvasProps {
  sections: BlockData[];
  selectedBlockId: string | null;
  portfolioId: string;
  portfolioTitle: string;
  onSelectBlock: (id: string) => void;
  onRemoveBlock: (id: string) => void;
  onToggleEnabled: (id: string) => void;
  onOpenEditModal: (blockId: string) => void;
}

// Map block types to icons
const iconMap: Record<string, React.ReactElement> = {
  hero: <ImageIcon />,
  mission_vision: <FlagIcon />,
  metrics: <BarChartIcon />,
  case_studies: <WorkIcon />,
  offerings: <CardGiftcardIcon />,
  gallery: <PhotoLibraryIcon />,
  testimonials: <FormatQuoteIcon />,
  rich_text: <TextFieldsIcon />,
  contact_form: <EmailIcon />,
  team: <GroupIcon />,
  past_impact: <TimelineIcon />,
  audience_demographics: <PeopleIcon />,
  media_reach: <CampaignIcon />,
  brand_visibility: <VisibilityIcon />,
  video_showcase: <PlayCircleIcon />,
  links: <LinkIcon />,
  programme_initiatives: <AccountTreeIcon />,
  artist: <ArtistSearchIcon />
};

interface SortableBlockItemProps {
  block: BlockData;
  isSelected: boolean;
  isChecked: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onToggleEnabled: () => void;
  onOpenEdit: () => void;
  onToggleCheck: () => void;
}

const SortableBlockItem: React.FC<SortableBlockItemProps> = ({
  block,
  isSelected,
  isChecked,
  onSelect,
  onRemove,
  onToggleEnabled,
  onOpenEdit,
  onToggleCheck
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const blockConfig = getBlockByType(block.type);
  const blockLabel = blockConfig?.label || block.type;
  const blockIcon = iconMap[block.type] || <ImageIcon />;

  // Extract title/subtitle from block data
  const getBlockTitle = () => {
    const MAX_LENGTH = 60;

    if (block.data && typeof block.data === 'object' && 'title' in block.data) {
      return (block.data as any).title?.slice(0, MAX_LENGTH);
    }

    if (block.type === 'rich_text' && (block.data as any)?.content) {
      const content = (block.data as any).content
        .replace(/<[^>]+>/g, '')
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean);
      return content[0]?.slice(0, MAX_LENGTH) || null;
    }

    if (block.type === 'image_highlight' && (block.data as any)?.primary) {
      return (block.data as any).primary?.slice(0, MAX_LENGTH);
    }

    if (block.type === 'metrics' && Array.isArray((block.data as any)?.metrics)) {
      const metrics = (block.data as any).metrics;
      if (metrics.length > 0 && metrics[0]?.label) {
        return metrics[0].label?.slice(0, MAX_LENGTH);
      }
    }

    if (block.type === 'past_impact' && (block.data as any)?.year) {
      return `${(block.data as any).year} Season`;
    }

    if (block.title) return block.title?.slice(0, MAX_LENGTH);

    return null;
  };

  const blockTitle = getBlockTitle();

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 2,
        bgcolor: isSelected ? '#3A9188' : '#34495E',
        border: `2px solid ${isSelected ? '#2ECC71' : '#3A9188'}`,
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: '#2ECC71',
          transform: 'translateX(4px)'
        }
      }}
      onClick={onOpenEdit}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Drag Handle */}
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: 'grab',
              color: '#A4B0BE',
              display: 'flex',
              '&:active': { cursor: 'grabbing' }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <DragIcon />
          </Box>

          {/* Block Icon */}
          <Box sx={{ color: '#3A9188', display: 'flex' }}>
            {blockIcon}
          </Box>

          {/* Block Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#ffffff'
              }}
            >
              {blockLabel}
            </Typography>
            {blockTitle && (
              <Typography
                variant="body2"
                sx={{
                  color: '#A4B0BE',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                "{blockTitle}"
              </Typography>
            )}
          </Box>

          {/* Enabled Chip */}
          <Chip
            label={block.enabled ? 'Visible' : 'Hidden'}
            size="small"
            sx={{
              bgcolor: block.enabled ? '#2ECC71' : '#7F8C8D',
              color: '#ffffff',
              fontWeight: 600
            }}
          />

          {/* Checkbox for selection */}
          <Checkbox
            checked={isChecked}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              onToggleCheck();
            }}
            sx={{
              color: '#A4B0BE',
              '&.Mui-checked': {
                color: '#2ECC71'
              }
            }}
          />

          {/* Toggle Enabled */}
          <Switch
            checked={block.enabled}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              onToggleEnabled();
            }}
            size="small"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#2ECC71'
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                bgcolor: '#2ECC71'
              }
            }}
          />

          {/* Delete Button */}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            sx={{ color: '#E57373' }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

const BlockCanvas: React.FC<BlockCanvasProps> = ({
  sections,
  selectedBlockId,
  portfolioId,
  portfolioTitle,
  onSelectBlock,
  onRemoveBlock,
  onToggleEnabled,
  onOpenEditModal
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState<string | null>(null);
  const [selectedBlockIds, setSelectedBlockIds] = useState<string[]>([]);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const handleDeleteClick = (blockId: string) => {
    setBlockToDelete(blockId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (blockToDelete) {
      onRemoveBlock(blockToDelete);
    }
    setDeleteDialogOpen(false);
    setBlockToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setBlockToDelete(null);
  };

  const handleToggleCheck = (blockId: string) => {
    setSelectedBlockIds(prev => {
      if (prev.includes(blockId)) {
        return prev.filter(id => id !== blockId);
      } else {
        return [...prev, blockId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedBlockIds.length === sections.length) {
      setSelectedBlockIds([]);
    } else {
      setSelectedBlockIds(sections.map(block => block.id));
    }
  };

  const handleCopyBlocks = () => {
    setCopyModalOpen(true);
  };

  const handleCopySuccess = (targetPortfolioTitle: string) => {
    setSnackbarMessage(`${selectedBlockIds.length} block${selectedBlockIds.length !== 1 ? 's' : ''} copied to "${targetPortfolioTitle}"`);
    setSnackbarOpen(true);
    setSelectedBlockIds([]);
  };

  if (sections.length === 0) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#A4B0BE',
          gap: 2,
          p: 4
        }}
      >
        <EmptyIcon sx={{ fontSize: 64, opacity: 0.3 }} />
        <Typography variant="h6" align="center" sx={{ color: '#ffffff' }}>
          No blocks added yet
        </Typography>
        <Typography variant="body2" align="center" sx={{ color: '#A4B0BE' }}>
          Add blocks from the library on the left to start building your portfolio
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 2 }}>
        {/* Header with block count and copy button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: '#A4B0BE',
              fontWeight: 600
            }}
          >
            {sections.length} {sections.length === 1 ? 'Block' : 'Blocks'}
            {selectedBlockIds.length > 0 && (
              <Typography component="span" sx={{ ml: 1, color: '#2ECC71' }}>
                ({selectedBlockIds.length} selected)
              </Typography>
            )}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {sections.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleSelectAll}
                sx={{
                  color: '#A4B0BE',
                  borderColor: '#A4B0BE',
                  '&:hover': {
                    borderColor: '#2ECC71',
                    color: '#2ECC71'
                  }
                }}
              >
                {selectedBlockIds.length === sections.length ? 'Deselect All' : 'Select All'}
              </Button>
            )}

            <Button
              size="small"
              variant="contained"
              disabled={selectedBlockIds.length === 0}
              onClick={handleCopyBlocks}
              startIcon={<CopyIcon />}
              sx={{
                bgcolor: '#3A9188',
                '&:hover': {
                  bgcolor: '#2ECC71'
                },
                '&:disabled': {
                  bgcolor: '#7F8C8D',
                  color: '#A4B0BE'
                }
              }}
            >
              Copy Selected Blocks
            </Button>
          </Box>
        </Box>

        {/* Block list */}
        {sections.map((block) => (
          <SortableBlockItem
            key={block.id}
            block={block}
            isSelected={selectedBlockId === block.id}
            isChecked={selectedBlockIds.includes(block.id)}
            onSelect={() => onSelectBlock(block.id)}
            onRemove={() => handleDeleteClick(block.id)}
            onToggleEnabled={() => onToggleEnabled(block.id)}
            onOpenEdit={() => onOpenEditModal(block.id)}
            onToggleCheck={() => handleToggleCheck(block.id)}
          />
        ))}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Block</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this block? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Blocks Modal */}
      <CopyBlocksModal
        open={copyModalOpen}
        onClose={() => setCopyModalOpen(false)}
        selectedBlockIds={selectedBlockIds}
        currentPortfolioId={portfolioId}
        currentPortfolioTitle={portfolioTitle}
        onSuccess={handleCopySuccess}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BlockCanvas;